/**
 * Sync service for background rating synchronization.
 * Handles queuing, batching, retries, and status updates.
 */

import { getUserId } from './user';
import {
  addToSyncQueue,
  getSyncQueue,
  removeFromQueueBatch,
  getQueueLength,
  type RatingSyncData,
  type SyncQueueItem,
} from './sync-queue';

const SYNC_API_URL = import.meta.env.VITE_SYNC_API_URL ||
  import.meta.env.VITE_PHOTO_API_URL ||
  'https://where-in-the-world-photos.armincerf.workers.dev';

const BATCH_SIZE = 50;
const RETRY_DELAYS = [1000, 5000, 10000, 30000, 60000]; // Exponential backoff

export type SyncState = 'idle' | 'syncing' | 'synced' | 'error' | 'offline';

export interface SyncStatus {
  state: SyncState;
  pending: number;
  lastSyncAt: number | null;
  error: string | null;
}

type SyncListener = (status: SyncStatus) => void;

/**
 * Split array into chunks of specified size.
 */
function chunk<T>(arr: T[], size: number): T[][] {
  return Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
    arr.slice(i * size, i * size + size)
  );
}

class SyncService {
  private isSyncing = false;
  private syncTimeout: ReturnType<typeof setTimeout> | null = null;
  private retryCount = 0;
  private listeners: Set<SyncListener> = new Set();
  private abortController: AbortController | null = null;
  private status: SyncStatus = {
    state: 'idle',
    pending: 0,
    lastSyncAt: null,
    error: null,
  };

  /**
   * Queue a rating change for sync.
   */
  async queueRating(countryCode: string, rating: number): Promise<void> {
    await addToSyncQueue({
      type: 'rating',
      data: {
        countryCode,
        rating,
        updatedAt: Date.now(),
      },
    });

    await this.updatePendingCount();
    this.scheduleSync();
  }

  /**
   * Subscribe to sync status changes.
   */
  subscribe(listener: SyncListener): () => void {
    this.listeners.add(listener);
    // Immediately notify with current status
    listener(this.status);
    return () => this.listeners.delete(listener);
  }

  /**
   * Get current sync status.
   */
  getStatus(): SyncStatus {
    return { ...this.status };
  }

  /**
   * Schedule a sync attempt (debounced).
   */
  scheduleSync(delayMs = 1000): void {
    if (this.syncTimeout) {
      clearTimeout(this.syncTimeout);
    }
    this.syncTimeout = setTimeout(() => this.sync(), delayMs);
  }

  /**
   * Force immediate sync attempt.
   */
  async syncNow(): Promise<boolean> {
    if (this.syncTimeout) {
      clearTimeout(this.syncTimeout);
      this.syncTimeout = null;
    }
    return this.sync();
  }

  /**
   * Attempt to sync queued items.
   * Uses AbortController to cancel in-flight requests if a new sync is started.
   */
  private async sync(): Promise<boolean> {
    if (this.isSyncing) return false;

    if (!navigator.onLine) {
      this.updateStatus({ state: 'offline', error: null });
      return false;
    }

    // Cancel any in-flight requests from previous sync
    this.abortController?.abort();
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    this.isSyncing = true;
    this.updateStatus({ state: 'syncing', error: null });

    try {
      const queue = await getSyncQueue();

      if (queue.length === 0) {
        this.retryCount = 0;
        this.updateStatus({
          state: 'synced',
          pending: 0,
          lastSyncAt: Date.now(),
          error: null,
        });
        return true;
      }

      const userId = getUserId();
      const batches = chunk(queue, BATCH_SIZE);
      const syncedIds: string[] = [];

      for (const batch of batches) {
        // Check if sync was aborted
        if (signal.aborted) {
          return false;
        }

        const ratings: RatingSyncData[] = batch
          .filter(item => item.type === 'rating')
          .map(item => item.data);

        if (ratings.length === 0) {
          // All items in this batch synced (shouldn't happen, but handle gracefully)
          syncedIds.push(...batch.map(b => b.id));
          continue;
        }

        try {
          const response = await fetch(`${SYNC_API_URL}/api/sync`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId, ratings }),
            signal, // Pass abort signal to fetch
          });

          if (response.ok) {
            syncedIds.push(...batch.map(b => b.id));
            this.retryCount = 0;
          } else {
            const errorData = await response.json().catch(() => ({}));
            const errorMsg = (errorData as { error?: string }).error || `HTTP ${response.status}`;
            console.warn('Sync failed:', errorMsg);

            // Remove already synced items before retrying
            if (syncedIds.length > 0) {
              await removeFromQueueBatch(syncedIds);
            }

            // Don't remove failed items - they'll be retried
            this.scheduleRetry();
            return false;
          }
        } catch (error) {
          // Don't retry if aborted intentionally
          if (error instanceof Error && error.name === 'AbortError') {
            return false;
          }

          console.warn('Sync error:', error);

          // Remove already synced items before retrying
          if (syncedIds.length > 0) {
            await removeFromQueueBatch(syncedIds);
          }

          this.scheduleRetry();
          return false;
        }
      }

      // Remove all synced items
      if (syncedIds.length > 0) {
        await removeFromQueueBatch(syncedIds);
      }

      await this.updatePendingCount();

      // Check if more items were queued during sync
      const remainingCount = await getQueueLength();
      if (remainingCount > 0) {
        this.scheduleSync();
      } else {
        this.updateStatus({
          state: 'synced',
          pending: 0,
          lastSyncAt: Date.now(),
          error: null,
        });
      }

      return true;
    } catch (error) {
      // Don't update status if aborted intentionally
      if (error instanceof Error && error.name === 'AbortError') {
        return false;
      }

      console.error('Sync error:', error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      this.updateStatus({ state: 'error', error: errorMsg });
      this.scheduleRetry();
      return false;
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Schedule retry with exponential backoff.
   */
  private scheduleRetry(): void {
    const delay = RETRY_DELAYS[Math.min(this.retryCount, RETRY_DELAYS.length - 1)];
    this.retryCount++;
    this.scheduleSync(delay);
  }

  /**
   * Update pending count from queue.
   */
  private async updatePendingCount(): Promise<void> {
    const pending = await getQueueLength();
    if (this.status.pending !== pending) {
      this.updateStatus({ pending });
    }
  }

  /**
   * Update status and notify listeners.
   */
  private updateStatus(partial: Partial<SyncStatus>): void {
    this.status = { ...this.status, ...partial };
    this.listeners.forEach((fn) => {
      fn(this.status);
    });
  }

  /**
   * Initialize sync service.
   * Call once on app startup.
   */
  init(): void {
    // Update pending count
    this.updatePendingCount();

    // Try to sync on load
    this.scheduleSync();

    // Sync when coming back online
    window.addEventListener('online', () => {
      this.updateStatus({ state: 'idle', error: null });
      this.sync();
    });

    window.addEventListener('offline', () => {
      this.updateStatus({ state: 'offline' });
    });

    // Attempt sync before page unload
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        // Try a final sync when leaving
        this.sync();
      }
    });
  }

  /**
   * Clean up the sync service.
   * Aborts any in-flight requests and clears scheduled syncs.
   */
  destroy(): void {
    // Abort any in-flight requests
    this.abortController?.abort();
    this.abortController = null;

    // Clear any scheduled sync
    if (this.syncTimeout) {
      clearTimeout(this.syncTimeout);
      this.syncTimeout = null;
    }

    // Clear listeners
    this.listeners.clear();
  }
}

// Singleton instance
export const syncService = new SyncService();
